<script>
import { apSecurityState, DEFAULT_AP_PASSWORD } from '../js/wifiValidation.js';
import { isWarningDismissed, dismissWarning } from '../js/dismissedWarnings.js';

/**
 * Three states, not two (MDL-361).
 *
 * Units now ship with a known shared passphrase, so "a password is set" no
 * longer means "this network is private". Going quiet the moment the default
 * is present would hide exactly the thing the owner needs to act on, so the
 * warning softens rather than disappears, and clears only on a passphrase
 * they chose themselves.
 *
 * Dismissible since MDL-393. The dismissal is remembered per state *and* per
 * device, so putting away "still on the shipped passphrase" cannot silence
 * "this network has no passphrase at all" — nor hide either one for a
 * different device the owner has never seen.
 */
export default {
  name: 'OpenApWarning',

  props: {
    settings: {
      type: Object,
      default: null,
    },

    // Identifies the device to this editor — a base URL is enough. Optional:
    // the on-device editor is served by the device, so its storage origin is
    // already per-device and needs no extra scoping.
    deviceKey: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      defaultApPassword: DEFAULT_AP_PASSWORD,
      // Mirrors storage. Held in data so dismissing re-renders immediately
      // rather than waiting for something else to touch the component.
      dismissedStates: {},
    };
  },

  computed: {
    // 'open' | 'default' | null
    //
    // Derived from the shared apSecurityState (MDL-387) so this banner and the
    // onboarding WiFi step cannot drift apart again — they previously each
    // computed their own answer, and onboarding never grew the 'default' state
    // this component added in MDL-361.
    //
    // 'invalid' renders nothing here, which preserves this component's
    // long-standing behaviour: it sits above the editor at all times, and the
    // WiFi form's own field validation already reports an out-of-range
    // passphrase where the owner is typing it. (Onboarding does surface it,
    // because there it also blocks the Next button.)
    state() {
      if (!this.settings) return null;
      const security = apSecurityState(this.settings);
      return security === 'open' || security === 'default' ? security : null;
    },

    dismissed() {
      if (!this.state) return false;
      // Consult data first so a dismissal in this session takes effect even
      // where storage is unavailable and nothing was persisted.
      if (this.dismissedStates[this.state]) return true;
      return isWarningDismissed(this.state, this.deviceKey);
    },

    visible() {
      return !!this.state && !this.dismissed;
    },
  },

  methods: {
    dismiss() {
      if (!this.state) return;
      this.dismissedStates = { ...this.dismissedStates, [this.state]: true };
      dismissWarning(this.state, this.deviceKey);
    },
  },
};
</script>

<template>
  <div
    v-if="visible"
    role="alert"
    :class="['alert rounded-none', state === 'open' ? 'alert-warning' : 'alert-info']"
    data-testid="open-ap-warning"
  >
    <span v-if="state === 'open'">
      <strong>Security warning:</strong>
      No password is set for broadcast mode — Midulate may be broadcasting an open WiFi network.
      Set a password, or turn broadcast mode off, in Settings.
    </span>
    <span v-else>
      <strong>Still using the default password for broadcast mode WiFi</strong> (<code>{{ defaultApPassword }}</code>) —
      it is printed in the manual, so anyone can look it up.
      Set your own in Settings to make this network private.
    </span>

    <!-- No btn-ghost: it sets its own text colour from base-content, which on
         the on-device editor's theme rendered a light glyph on the alert's
         light background. Carrying no colour class at all means the glyph
         inherits the alert's own content colour, so it stays legible whatever
         the theme and whichever alert variant is showing. -->
    <button
      type="button"
      class="ml-auto shrink-0 cursor-pointer opacity-70 hover:opacity-100 px-1 leading-none text-lg"
      aria-label="Dismiss"
      data-testid="open-ap-warning-dismiss"
      @click="dismiss"
    >
      ✕
    </button>
  </div>
</template>
